import type { CurrentUser } from "@/features/user/types";
import type { ApiDataResponse } from "@/types/api-types";
import { proxyImg } from "@/utils/tmp";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { api } from "../api-client";
import {
  IdentifierType,
  determineIdentifierType,
  loginSchema,
  resetPasswordSchema,
  signupSchema,
} from "./schema";
import {
  createSession,
  deleteSessionTokenCookie,
  setSessionTokenCookie,
} from "./session";

const getUserByToken = createServerFn({ method: "GET" })
  .validator(z.string())
  .handler<CurrentUser>(async ({ data: token }) => {
    const response = await api
      .post("user/getUserProfile", {
        headers: {
          Authorization: `Bearer ${token}`,
          Token: token,
        },
      })
      .json<
        ApiDataResponse<{
          userId: string;
          userName: string;
          email: string;
          nickName: string;
          introduction: string;
          avatarUrl: string;
        }>
      >();

    return {
      userId: response.data.userId,
      username: response.data.userName,
      email: response.data.email,
      nickname: response.data.nickName ?? response.data.userName,
      bio: response.data.introduction ?? "",
      avatarUrl: proxyImg(
        response.data.avatarUrl ??
          `https://api.dicebear.com/9.x/pixel-art/svg?seed=${response.data.userId}`,
      ),
    };
  });

export async function validateTokenWithApi(
  token: string,
): Promise<{ userId: string } | null> {
  try {
    const user = await getUserByToken({ data: token });
    return { userId: user.userId };
  } catch (error) {
    console.error("Error validating token with API:", error);
    return null;
  }
}

const loginWithBasicCredentials = createServerFn({ method: "POST" })
  .validator(loginSchema)
  .handler(async ({ data }) => {
    const identifierType = determineIdentifierType(data.identifier);
    if (identifierType !== IdentifierType.Email) {
      throw new Error("Only email login is supported");
    }

    const tokenResponse = await api
      .post("open-api/auth/login/mail", {
        json: {
          mail: data.identifier,
          password: data.password,
        },
      })
      .json<ApiDataResponse<string>>();

    const sessionToken = tokenResponse.data;

    const user = await getUserByToken({ data: sessionToken });

    const session = await createSession(sessionToken, user.userId);
    setSessionTokenCookie(sessionToken, session.expiresAt);

    return session;
  });

const signupWithBasicCredentials = createServerFn({ method: "POST" })
  .validator(signupSchema)
  .handler(async ({ data }) => {
    const response = await api
      .post("open-api/auth/register/newAccount", {
        json: {
          mail: data.email,
          userName: data.username,
          password: data.password,
          code: data.otp,
        },
      })
      .json<ApiDataResponse<never>>();

    return response.ok;
  });

const resetPassword = createServerFn({ method: "POST" })
  .validator(resetPasswordSchema)
  .handler(async ({ data }) => {
    const response = await api.post("open-api/auth/reset/mail", {
      json: {
        mail: data.email,
        newPassword: data.newPassword,
        code: data.code,
      },
    });
    return response.ok;
  });

const logout = createServerFn({ method: "POST" }).handler(() => {
  deleteSessionTokenCookie();
});

export const authService = {
  loginWithBasicCredentials,
  signupWithBasicCredentials,
  resetPassword,
  logout,
  getUserByToken,
  validateTokenWithApi,
};
